// src/components/ProtectedRoute.js — Gate that requires an authenticated
// session AND routes by role: partner users only ever see /portal/*; every
// other role only ever sees the main app. Pass partnerOnly to gate the
// /portal/* tree.
import { Navigate, useLocation } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'

export default function ProtectedRoute({ children, partnerOnly = false }) {
  const { user, profile, loading, signOut } = useAuth()
  const location = useLocation()

  if (loading) {
    return (
      <div className="loading-screen">
        <div className="spinner spinner-lg" />
      </div>
    )
  }

  if (!user) {
    return <Navigate to="/login" replace state={{ from: location }} />
  }

  // New users (invited, or given a temporary password) must choose their own
  // password before using the app. users.password_set_at is set by
  // /api/auth/set-password; existing users were backfilled (migration 051).
  if (profile && !profile.password_set_at) {
    return <Navigate to="/set-password" replace />
  }

  // Deactivated by an admin (Settings -> Team & Access). The API refuses
  // them anyway; this explains why instead of showing a broken app.
  if (profile?.deactivated_at) {
    return (
      <div className="loading-screen" style={{ flexDirection: 'column', gap: 12, textAlign: 'center', padding: 24 }}>
        <div style={{ fontSize: 16, fontWeight: 600 }}>This account has been deactivated</div>
        <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>Ask an RGA admin if you need access again.</div>
        <button className="btn btn-secondary btn-sm" onClick={signOut}>Sign out</button>
      </div>
    )
  }

  const isPartner = profile?.role === 'partner'

  if (partnerOnly && !isPartner) {
    return <Navigate to="/" replace />
  }

  if (!partnerOnly && isPartner) {
    return <Navigate to="/portal" replace />
  }

  return children
}
